import { axiosInstance } from "@retro-ia/shared/axios";
import { isAxiosError } from "axios";
import type {
  CreateAprendizProgramaInput,
  UpdateAprendizProgramaInput,
} from "@/lib/validaciones/aprendicesProgramas.validacion";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";

interface Input {
  idPrograma: number;
  archivo: File;
}

export const useAprendicesProgramas = (
  page: number,
  limit: number,
  search: string,
) => {
  return useQuery({
    queryKey: ["aprendicesProgramas", page, limit, search],
    queryFn: async () => {
      const res = await axiosInstance.get("/aprendices-programas", {
        params: { page, limit, search },
      });
      return res.data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useAprendicesByPrograma = (
  page: number,
  limit: number,
  search: string,
  idPrograma: number,
) => {
  return useQuery({
    queryKey: ["aprendicesByPrograma", idPrograma, page, limit, search],
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/aprendices-programas/programa/${idPrograma}`,
        {
          params: { page, limit, search },
        },
      );
      return res.data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useRegisterAprendicesPrograma = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAprendizProgramaInput) => {
      const res = await axiosInstance.post("/aprendices-programas", data);
      return res.data;
    },
    onSuccess: (res) => {
      if (res?.mensaje) {
        toast.error(res.errores[0]);
      } else {
        queryClient.invalidateQueries({ queryKey: ["aprendicesProgramas"] });
        toast.success("Aprendis programa creado exitosamente");
      }
    },
    onError: (error: unknown) => {
      if (
        isAxiosError<{ mensaje?: string; errores?: string[] }>(error) &&
        error.response?.data?.mensaje &&
        error.response.data.errores?.length
      ) {
        toast.error(error.response.data.errores[0]);
        return;
      }
      toast.error("Error al crear el Aprendiz programa");
    },
  });
};

export const useDeleteAprendizPrograma = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await axiosInstance.put(`/aprendices-programas/${id}`, {
        withCredentials: true,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success("El aprendiz programa fue eliminado.");
      queryClient.invalidateQueries({ queryKey: ["aprendicesProgramas"] });
    },
    onError: () => {
      toast.error("Error al eliminar el aprendiz programa.");
    },
  });
};

export const useUpdateAprendizPrograma = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateAprendizProgramaInput) => {
      const res = await axiosInstance.patch(
        `/aprendices-programas/${data.id}`,
        data,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["aprendicesProgramas"] });
      toast.success("Aprendiz programa actualizado exitosamente");
    },
    onError: () => {
      toast.error("Error al actualizar el aprendiz programa");
    },
  });
};

export function useCargaMasivaAprendicesProgramas() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ idPrograma, archivo }: Input) => {
      const formData = new FormData();
      formData.append("archivo", archivo);

      const res = await axiosInstance.post(
        `/aprendices-programas/carga-masiva?idPrograma=${idPrograma}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      return res.data;
    },
    onSuccess: (res) => {
      if (res?.mensaje === "Error de cupo") {
        toast.error(res.errores[0]);
      } else {
        toast.success("Aprendices inscritos exitosamente");
        queryClient.invalidateQueries({ queryKey: ["aprendicesProgramas"] });
      }
    },
    onError: (error: unknown) => {
      if (
        isAxiosError<{ mensaje?: string; errores?: string[] }>(error) &&
        error.response?.data?.mensaje &&
        error.response.data.errores?.length
      ) {
        toast.error(error.response.data.errores[0]);
        return;
      }
      toast.error("Error al cargar el archivo");
    },
  });
}
